import { roofPanelPhoto } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { STAGE_LABELS, STAGES, ACTION_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, ArrowLeft, ExternalLink, MapPin, Phone, Mail, Zap, Battery, Plug, CreditCard, Wallet, CheckCircle2, Clock, Pencil, X, Plus, Star, Unlock, Send, AlertTriangle, Copy, HardHat, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PartnerQuote } from "../components/PartnerQuote";
import RoofMapView from "../components/RoofMapView";
import { AssignInstallersDialog } from "../components/AssignInstallersDialog";

const INST_ST_COLORS = { proposto: "#F59E0B", accettato: "#60A5FA", rifiutato: "#EF4444", selezionato: "#34D399", non_selezionato: "#9CA3AF" };
const INST_ST_LABELS = { proposto: "Proposto", accettato: "Ha accettato", rifiutato: "Ha rifiutato", selezionato: "Selezionato", non_selezionato: "Non selezionato" };

const STATUS_META = {
  in_attesa: { label: "In attesa", cls: "border-border text-muted-foreground" },
  firmato: { label: "Contratto firmato", cls: "border-blue-500/40 bg-blue-500/10 text-blue-500" },
  caparra_pagata: { label: "Ordine confermato", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" },
};
const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const num = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: 1 }));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "");

export default function AreaPartnerLead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [showInstallers, setShowInstallers] = useState(false);
  const [installers, setInstallers] = useState([]);

  const reload = () => api.get(`/partner/leads/${id}`).then((r) => setLead(r.data)).catch(() => setErr(true));
  const loadInstallers = () => api.get(`/leads/${id}/installers`).then((r) => setInstallers(r.data.assigned || [])).catch(() => setInstallers([]));
  useEffect(() => { reload(); loadInstallers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const newOpportunity = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner/leads/${id}/new-opportunity`);
      toast.success("Nuova opportunità creata (nuovo indirizzo)");
      navigate(`/area-partner/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const switchPrev = async (pid) => {
    if (pid === lead?.active_preventivo_id || busy) return;
    setBusy(true);
    try { await api.put(`/partner/leads/${id}/preventivi/${pid}/activate`); await reload(); toast.success("Preventivo attivato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addPrev = async () => {
    setBusy(true);
    try { await api.post(`/partner/leads/${id}/preventivi`); await reload(); toast.success("Nuovo preventivo creato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const delPrev = async (pid) => {
    setBusy(true);
    try { await api.delete(`/partner/leads/${id}/preventivi/${pid}`); await reload(); toast.success("Preventivo eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const renamePrev = async (pid, label) => {
    setBusy(true);
    try { await api.put(`/partner/leads/${id}/preventivi/${pid}/label`, { label }); await reload(); setRenaming(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleCons = async (pid) => {
    setBusy(true);
    try { await api.put(`/partner/leads/${id}/preventivi/${pid}/consigliato`); await reload(); toast.success("Preventivo consigliato aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-3">
      <p className="text-sm text-muted-foreground">Lead non trovato o non accessibile.</p>
      <Button variant="outline" onClick={() => navigate("/area-partner")}><ArrowLeft className="h-4 w-4 mr-1.5" /> Torna all'area partner</Button>
    </div>
  );
  if (!lead) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const st = STATUS_META[lead.status] || STATUS_META.in_attesa;
  const cfg = lead.configurazione || {};
  const stageIdx = STAGES.indexOf(lead.stage);
  const activePrevPartner = (lead.preventivi || []).find((p) => p.id === lead.active_preventivo_id) || {};
  const partnerEvents = activePrevPartner.events || [];
  const fmtDT = (x) => { try { return new Date(x).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="area-partner-lead">
      <header className="sticky top-0 z-20 h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate("/area-partner")} data-testid="partner-lead-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Area partner</Button>
        <Button variant="outline" size="sm" onClick={newOpportunity} disabled={busy} className="gap-1.5" data-testid="partner-new-opportunity-btn"><Copy className="h-4 w-4" /> Nuova opportunità</Button>
        <div className="flex items-center gap-2">
          {lead.active_locked && lead.manage_preventivi && (
            <span className="text-xs text-muted-foreground mr-1" data-testid="partner-unlock-admin-only">Sblocco riservato all'amministratore</span>
          )}
          {lead.editable && (
            <Button size="sm" onClick={() => setEditOpen(true)} data-testid="partner-edit-quote-btn"><Pencil className="h-4 w-4 mr-1.5" /> Modifica preventivo</Button>
          )}
          {lead.outlook_token && (
            <a href={`/p/${lead.outlook_token}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="partner-lead-proposal"><ExternalLink className="h-4 w-4 mr-1.5" /> Apri proposta</Button>
            </a>
          )}
        </div>
      </header>

      <div className="p-4 lg:p-8 space-y-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading font-bold">{lead.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
              {lead.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lead.city}</span>}
              {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {lead.phone}</span>}
              {lead.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {lead.email}</span>}
            </div>
          </div>
          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${st.cls}`} data-testid="partner-lead-status">{st.label}</span>
        </div>

        {/* Avanzamento fase */}
        <Card className="rounded border-border p-4" data-testid="partner-lead-stages">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Avanzamento</div>
          <div className="flex items-center gap-1">
            {STAGES.map((s, i) => (
              <div key={s} className="flex-1 flex flex-col items-center gap-1.5">
                <div className={`h-1.5 w-full rounded-full ${i <= stageIdx ? "bg-primary" : "bg-secondary"}`} />
                <span className={`text-[10px] text-center leading-tight ${i <= stageIdx ? "text-foreground font-medium" : "text-muted-foreground"}`}>{STAGE_LABELS[s]}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Provvigione + valore */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="rounded border-border p-4" data-testid="partner-lead-value">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Valore proposta (IVA incl.)</div>
            <div className="font-heading font-bold text-xl">{eur(lead.prezzo_iva || lead.prezzo)}</div>
          </Card>
          <Card className="rounded border-primary/40 bg-primary/5 p-4" data-testid="partner-lead-comm-stimata">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Provvigione stimata</div>
            <div className="font-heading font-bold text-xl text-primary">{eur(lead.commission_eur)}{lead.commission_pct ? <span className="text-xs font-normal text-muted-foreground ml-1">{lead.commission_pct}%</span> : null}</div>
          </Card>
          <Card className={`rounded p-4 ${lead.commission_maturata > 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`} data-testid="partner-lead-comm-maturata">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Wallet className="h-3 w-3" /> Provvigione maturata</div>
            <div className={`font-heading font-bold text-xl ${lead.commission_maturata > 0 ? "text-emerald-500" : "text-muted-foreground"}`}>{eur(lead.commission_maturata)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{lead.commission_maturata > 0 ? "Ordine confermato" : "Alla conferma dell'ordine"}</div>
          </Card>
        </div>

        {/* Preventivi (multi) */}
        {lead.manage_preventivi && (lead.preventivi?.length > 0) && (
          <Card className="rounded border-border p-4" data-testid="partner-preventivi-bar">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Preventivi</div>
            {lead.active_locked && <p className="text-xs text-amber-600 mb-3" data-testid="partner-preventivo-locked-note">Il preventivo inviato è bloccato. Lo sblocco è riservato all'amministratore: per modificarlo contatta l'amministratore, oppure crea una variante con "Nuovo preventivo".</p>}
            {activePrevPartner.sent_at && activePrevPartner.dirty_since_send && !lead.active_locked && (
              <div className="rounded border border-orange-500/50 bg-orange-500/10 p-3 mb-3 flex items-start gap-2" data-testid="partner-preventivo-dirty-warning">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">La proposta inviata al cliente <b className="text-orange-600">non è aggiornata</b>: hai modificato il preventivo dopo l'invio. Re-invia la proposta per aggiornare link e PDF.</p>
              </div>
            )}
            {partnerEvents.length > 0 && (
              <div className="rounded border border-border bg-secondary/30 p-3 mb-3" data-testid="partner-preventivo-history">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Cronologia versione</div>
                <ul className="space-y-1">
                  {partnerEvents.slice().reverse().map((ev, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`partner-preventivo-history-item-${i}`}>
                      {ev.action === "inviato" ? <Send className="h-3 w-3 text-primary shrink-0" /> : <Unlock className="h-3 w-3 text-amber-500 shrink-0" />}
                      <span className="text-foreground font-medium">{ev.action === "inviato" ? "Proposta inviata al cliente" : "Preventivo sbloccato"}</span>
                      {ev.by ? <span>· da <b className="text-foreground">{ev.by}</b></span> : null}
                      {ev.at ? <span>· {fmtDT(ev.at)}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {lead.preventivi.map((p) => {
                const isActive = p.id === lead.active_preventivo_id;
                return (
                  <div key={p.id} className={`flex items-center rounded-full border pl-1 ${isActive ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`partner-preventivo-tab-${p.id}`}>
                    {renaming === p.id ? (
                      <input autoFocus defaultValue={p.label} disabled={busy}
                        onBlur={(e) => renamePrev(p.id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") renamePrev(p.id, e.target.value); if (e.key === "Escape") setRenaming(null); }}
                        className="bg-transparent text-sm font-medium px-2 py-1 w-32 outline-none" data-testid={`partner-preventivo-rename-input-${p.id}`} />
                    ) : (
                      <button type="button" onClick={() => switchPrev(p.id)} disabled={busy} className={`pl-2 pr-1 py-1.5 text-sm font-medium ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`} data-testid={`partner-preventivo-tab-btn-${p.id}`}>
                        {p.label}{p.prezzo ? <span className="text-xs font-normal text-muted-foreground ml-1">{eur(p.prezzo)}</span> : null}
                      </button>
                    )}
                    {renaming !== p.id && (
                      <button type="button" onClick={() => toggleCons(p.id)} disabled={busy} title={p.consigliato ? "Rimuovi da consigliato" : "Segna come consigliato al cliente"} className={`px-1 ${p.consigliato ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid={`partner-preventivo-consigliato-${p.id}`}><Star className="h-3 w-3" fill={p.consigliato ? "currentColor" : "none"} /></button>
                    )}
                    {renaming !== p.id && (
                      <button type="button" onClick={() => setRenaming(p.id)} title="Rinomina" className="px-1 text-muted-foreground hover:text-primary" data-testid={`partner-preventivo-rename-${p.id}`}><Pencil className="h-3 w-3" /></button>
                    )}
                    {lead.preventivi.length > 1 && (
                      <button type="button" onClick={() => delPrev(p.id)} disabled={busy} title="Elimina" className="pr-2 pl-0.5 text-muted-foreground hover:text-destructive" data-testid={`partner-preventivo-delete-${p.id}`}><X className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={addPrev} disabled={busy} className="rounded-full h-8 gap-1.5" data-testid="partner-preventivo-add-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Nuovo preventivo
              </Button>
            </div>
          </Card>
        )}

        {/* Configurazione impianto */}
        <Card className="rounded border-border p-4" data-testid="partner-lead-config">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Configurazione impianto</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ConfigItem icon={Zap} label="Potenza" value={cfg.pv_kwp ? `${cfg.pv_kwp} kWp${cfg.n_pannelli ? ` · ${cfg.n_pannelli} moduli` : ""}` : "—"} />
            <ConfigItem icon={Battery} label="Accumulo" value={cfg.storage_kwh ? `${cfg.storage_kwh} kWh${cfg.n_batterie ? ` · ${cfg.n_batterie} batt.` : ""}${cfg.batteria_tipo ? ` (${cfg.batteria_tipo})` : ""}` : "No"} />
            <ConfigItem icon={Plug} label="Ricarica" value={cfg.ricarica ? (cfg.ricarica_model || "Wallbox") : "No"} />
            <ConfigItem icon={CreditCard} label="Finanziamento" value={cfg.finanziamento?.attivo ? `${cfg.finanziamento.durata_mesi || ""} rate${cfg.finanziamento.rata_mensile ? ` · ${eur(cfg.finanziamento.rata_mensile)}/mese` : ""}` : "No"} />
          </div>
        </Card>

        {/* Tetto + Layout pannelli + Auto elettrica (sola lettura) */}
        {(lead.roof?.falde?.length > 0 || lead.ev_benefit?.enabled) && (
          <Card className="rounded border-border p-4" data-testid="partner-lead-roof-ev">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Tetto e beneficio auto elettrica</div>
            {lead.roof?.falde?.length > 0 && (
              <div className="mb-4" data-testid="partner-roof-view">
                <RoofMapView falde={lead.roof.falde} panelLayout={lead.roof.panel_layout} panelPhoto={roofPanelPhoto((!lead.panel_auto && lead.listino_panel) || lead.roof?.module)} pdfBg={lead.roof.pdf_bg} origin={lead.roof.center} drawMode={lead.roof.draw_mode} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {lead.roof.summary?.netta_m2 != null && <RoBox label="Sup. utilizzabile" value={`${num(lead.roof.summary.netta_m2)} m²`} />}
                  {lead.roof.panel_layout?.enabled && lead.roof.panel_layout?.summary?.count != null && (
                    <RoBox label="Pannelli (layout)" value={`${lead.roof.panel_layout.summary.count}`} highlight />
                  )}
                  {lead.roof.panel_layout?.enabled && lead.roof.panel_layout?.summary?.kwp != null && (
                    <RoBox label="Potenza (layout)" value={`${num(lead.roof.panel_layout.summary.kwp)} kWp`} highlight />
                  )}
                  {lead.roof.summary?.moduli_max != null && <RoBox label="Moduli max (stima)" value={`${lead.roof.summary.moduli_max}`} />}
                </div>
              </div>
            )}
            {lead.ev_benefit?.enabled && (
              <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="partner-ev-view">
                <div className="flex items-center gap-2 mb-3"><Plug className="h-4 w-4 text-emerald-500" strokeWidth={1.5} /><span className="text-sm font-semibold">Copertura costo km auto elettrica</span></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {lead.ev_benefit.model_label && <RoBox label="Modello" value={lead.ev_benefit.model_label} />}
                  {lead.ev_benefit.km_year != null && <RoBox label="Km / anno" value={num(lead.ev_benefit.km_year)} />}
                  {lead.ev_benefit.consumo_kwh_100 != null && <RoBox label="Consumo" value={`${lead.ev_benefit.consumo_kwh_100} kWh/100km`} />}
                  {lead.ev_benefit.annuo != null && <RoBox label="Risparmio / anno" value={eur(lead.ev_benefit.annuo)} highlight />}
                </div>
                {lead.ev_benefit.copertura_pct != null && (
                  <p className="text-[11px] text-muted-foreground mt-2" data-testid="partner-ev-detail">Coperti dal fotovoltaico ≈ {lead.ev_benefit.km_coperti} km/anno ({lead.ev_benefit.copertura_pct}%){lead.ev_benefit.totale != null ? ` · in ${lead.ev_benefit.anni} anni ${eur(lead.ev_benefit.totale)}` : ""}.</p>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Installatori in gara */}
        <Card className="rounded border-border p-4" data-testid="lead-installers-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><HardHat className="h-3 w-3 text-primary" /> Installatori in gara</div>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowInstallers(true)} data-testid="open-installers-dialog"><UserPlus className="h-3.5 w-3.5" /> Gestisci</Button>
          </div>
          {installers.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="installers-empty">Nessun installatore attivato per questo lead.</p>
          ) : (
            <div className="space-y-1.5" data-testid="installers-list">
              {installers.map((i) => (
                <div key={i.installer_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`installer-item-${i.installer_id}`}>
                  <span className="truncate">{i.ragione_sociale}</span>
                  <span className="text-[10px] rounded-full border px-2 py-0.5 shrink-0" style={{ borderColor: (INST_ST_COLORS[i.status] || "#9CA3AF") + "66", color: INST_ST_COLORS[i.status] || "#9CA3AF" }}>{INST_ST_LABELS[i.status] || i.status}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Timeline / stato di sviluppo */}
        <Card className="rounded border-border p-4" data-testid="partner-lead-timeline">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Stato di sviluppo</div>
          {(lead.timeline || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>
          ) : (
            <ol className="space-y-3">
              {lead.timeline.map((t) => (
                <li key={t.id} className="flex gap-3" data-testid={`partner-timeline-${t.id}`}>
                  <div className="flex flex-col items-center pt-0.5">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    <div className="w-px flex-1 bg-border mt-1" />
                  </div>
                  <div className="pb-1">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{ACTION_LABELS[t.type] || t.type} · {fmtDate(t.created_at)}</div>
                    <div className="text-sm">{t.note}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="partner-edit-quote-dialog">
          <DialogHeader><DialogTitle>Modifica preventivo — {lead.name}</DialogTitle></DialogHeader>
          {lead.self_token && (
            <PartnerQuote
              selfToken={lead.self_token}
              preAuthed
              prefill={{
                accumulo: !!cfg.storage_kwh,
                wallbox: !!cfg.ricarica,
                finanziamento: !!cfg.finanziamento?.attivo,
                n_pannelli: cfg.n_pannelli || "",
                n_batterie: cfg.n_batterie || "",
                durata_mesi: cfg.finanziamento?.durata_mesi ? String(cfg.finanziamento.durata_mesi) : "60",
              }}
              onDone={() => { setEditOpen(false); reload(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      <AssignInstallersDialog
        lead={{ ...lead, id }}
        open={showInstallers}
        onOpenChange={setShowInstallers}
        onChanged={loadInstallers}
      />
    </div>
  );
}

function ConfigItem({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-0.5"><Icon className="h-3 w-3" /> {label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function RoBox({ label, value, highlight }) {
  return (
    <div className="rounded border border-border bg-secondary/20 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>
      <div className={`text-sm font-semibold ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
